import React from "react";

import {
  INextGenDashboardChart,
  INextGenDashboardQuestion,
} from "../../services/zeos-api/contracts/Dashboard";
import { AskBar } from "./AskBar";
import { MidFiChip, MidFiChipTone } from "./MidFiChip";

interface IProps {
  chart: INextGenDashboardChart;
  children: React.ReactNode;
  /** Applies a canned question's range / compare preset. */
  onQuestionSelect?: (question: INextGenDashboardQuestion) => void;
  /** Tone of the top-right source chip; connected feeds read as "ok". */
  sourceTone?: MidFiChipTone;
  /** Extra chip next to the source chip, e.g. "Illustrative" on a pattern preview. */
  badge?: React.ReactNode;
  /** The Ask bar's current question text, when the page tracks it (see AskBar.selectedText). */
  selectedQuestion?: string | null;
}

// One chart per card (4019), mid-fi styling (UX-05): eyebrow, title, subtitle, Ask bar, then the
// chart. Known gaps render as footnotes under the chart rather than being hidden.
export const ChartCard = ({
  chart,
  children,
  onQuestionSelect,
  sourceTone = "ok",
  badge,
  selectedQuestion,
}: IProps) => (
  <section
    className="rounded-xl border border-mf-border bg-mf-card px-[22px] pb-3.5 pt-[18px] shadow-mf-card"
    aria-label={chart.title}
  >
    <div className="flex items-start justify-between gap-3.5">
      <div className="min-w-0 flex-1">
        {chart.eyebrow ? (
          <div className="text-[10.5px] font-extrabold uppercase tracking-[.09em] text-mf-faint">
            {chart.eyebrow}
          </div>
        ) : null}
        <h3 className="my-0.5 text-[16.5px] font-extrabold text-mf-ink">{chart.title}</h3>
        {chart.subtitle ? (
          <div className="text-[11px] font-semibold text-mf-sub">{chart.subtitle}</div>
        ) : null}
        <AskBar
          questions={chart.questions}
          answers={chart.answers}
          label={chart.title}
          onSelect={onQuestionSelect}
          selectedText={selectedQuestion}
        />
      </div>
      {chart.sourceLabel || badge ? (
        <div className="flex flex-none flex-wrap justify-end gap-2">
          {badge}
          {chart.sourceLabel ? <MidFiChip label={chart.sourceLabel} tone={sourceTone} /> : null}
        </div>
      ) : null}
    </div>
    <div className="mt-3">{children}</div>
    {chart.notes?.length ? (
      <ul className="mt-2 space-y-0.5 text-[11px] font-semibold text-mf-faint">
        {chart.notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    ) : null}
  </section>
);
